import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetPrintJobsQuery,
  useDeletePrintJobMutation,
  useUpdatePrintJobStatusMutation,
  PrintJob,
} from "../generated/graphql";
import { useAuth } from "../contexts/AuthContext";
import {
  getPrintUrlMissingErrorMessage,
  isPrintJobError,
  PDF_GENERATION_FAILED_MESSAGE,
} from "../utils/getPrintUrlMissingErrorMessage";

// 確認モーダルコンポーネント
interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="mb-6 text-gray-700">{message}</p>
        <div className="flex justify-end space-x-3">
          <button
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700"
            onClick={onCancel}
          >
            キャンセル
          </button>
          <button
            className="px-4 py-2 rounded-md text-white bg-red-500"
            onClick={onConfirm}
          >
            削除する
          </button>
        </div>
      </div>
    </div>
  );
};

const PrintJobsPage: React.FC = () => {
  // ドロップダウンメニューの状態
  const [showDropdown, setShowDropdown] = useState(false);

  // ドロップダウン外のクリックを検知するための参照
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { isAuthenticated, authState, logout } = useAuth();
  const navigate = useNavigate();

  // 認証チェック
  useEffect(() => {
    // ローカルストレージから直接確認（念のため）
    const storedAuth = localStorage.getItem("print_system_auth");
    const hasStoredAuth = !!storedAuth;

    if (!isAuthenticated && !hasStoredAuth) {
      navigate("/login");
    }
  }, [isAuthenticated, authState, navigate]);

  // ドロップダウン外のクリックを検知するイベントリスナー
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    // イベントリスナーを追加
    document.addEventListener("mousedown", handleClickOutside);

    // クリーンアップ関数
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { loading, error, data, refetch } = useGetPrintJobsQuery({
    variables: { printed: false },
  });

  const {
    loading: loadingPrinted,
    data: printedData,
    refetch: refetchPrinted,
  } = useGetPrintJobsQuery({
    variables: { printed: true },
  });

  // 削除ミューテーションを追加
  const [deletePrintJob] = useDeletePrintJobMutation({
    onCompleted: () => {
      // 削除成功後にデータを再取得
      refetch();
      refetchPrinted();
    },
  });

  // 印刷済みジョブをクリアするために削除ミューテーションを使用
  const [clearPrintedJobsState, setClearPrintedJobsState] = useState({
    execute: async () => {
      // 印刷済みジョブをすべて取得
      const printedJobs = completedJobs;

      // 各ジョブを削除
      for (const job of printedJobs) {
        try {
          await deletePrintJob({
            variables: {
              input: {
                printJobId: job.id,
              },
            },
          });
        } catch (error) {
          console.error(
            `ジョブ ${job.id} の削除中にエラーが発生しました:`,
            error
          );
          throw error;
        }
      }

      // 削除後にデータを再取得
      await refetchPrinted();
    },
    loading: false,
  });

  // clearPrintedJobs オブジェクトを修正
  const clearPrintedJobs = {
    execute: async () => {
      setClearPrintedJobsState((prev) => ({ ...prev, loading: true }));
      try {
        // 印刷済みジョブをすべて取得
        const printedJobs = completedJobs;

        // 各ジョブを削除
        for (const job of printedJobs) {
          await deletePrintJob({
            variables: {
              input: {
                printJobId: job.id,
              },
            },
          });
        }

        // 削除後にデータを再取得
        await refetchPrinted();
      } catch (error) {
        console.error("印刷済みジョブのクリア中にエラー:", error);
        throw error;
      } finally {
        setClearPrintedJobsState((prev) => ({ ...prev, loading: false }));
      }
    },
    loading: clearPrintedJobsState.loading,
  };

  // モーダル状態
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // 印刷ステータス更新ミューテーションを追加
  const [updatePrintJobStatus] = useUpdatePrintJobStatusMutation({
    onCompleted: () => {
      // 更新成功後にデータを再取得
      refetch();
      refetchPrinted();
    },
  });

  // ローディング中の表示
  if (loading && loadingPrinted) return <p>読み込み中...</p>;
  if (error) {
    if (error.message === "Require authentication") {
      navigate("/login");
      return null;
    }
    return <p>エラーが発生しました: {error.message}</p>;
  }

  // 印刷待機中のジョブ
  const waitingJobs = (data?.getPrintJobs.nodes || []).filter(
    (job): job is PrintJob => job !== null
  );

  // 印刷済みのジョブ
  const completedJobs = (printedData?.getPrintJobs.nodes || []).filter(
    (job): job is PrintJob => job !== null
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handlePrint = (job: PrintJob) => {
    if (isPrintJobError(job.status)) {
      return;
    }

    // URLが存在する場合は新しいタブでURLを開く
    if (job.url) {
      window.open(job.url, "_blank");

      // 印刷ステータスを更新
      updatePrintJobStatus({
        variables: {
          input: {
            printJobId: job.id,
            // lastPrintedAtプロパティは不要 - バックエンドで自動的に現在時刻が設定される
          },
        },
      }).catch((error) => {
        console.error("印刷ステータス更新エラー:", error);
      });
    } else {
      // URLが存在しない場合はエラーメッセージを表示
      setModalState({
        isOpen: true,
        title: "印刷エラー",
        message: getPrintUrlMissingErrorMessage(job.status),
        onConfirm: async () => {
          try {
            await deletePrintJob({
              variables: {
                input: {
                  printJobId: job.id,
                },
              },
            });
            setModalState((prev) => ({ ...prev, isOpen: false }));
          } catch (error) {
            console.error("削除エラー:", error);
            alert("削除中にエラーが発生しました。");
            setModalState((prev) => ({ ...prev, isOpen: false }));
          }
        },
      });
    }
  };

  // 日付をフォーマットする関数
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  // 印刷ジョブアイテムのコンポーネント
  const PrintJobItem = ({
    job,
    isSelected,
    onSelect,
    showPrintButton,
    isCompleted,
  }: {
    job: PrintJob;
    isSelected: boolean;
    onSelect: () => void;
    showPrintButton: boolean;
    isCompleted: boolean;
  }) => {
    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();

      setModalState({
        isOpen: true,
        title: "印刷ジョブの削除",
        message: `「${
          job.description || "タイトルなし"
        }」を削除してもよろしいですか？`,
        onConfirm: async () => {
          try {
            await deletePrintJob({
              variables: {
                input: {
                  printJobId: job.id,
                },
              },
            });
            setModalState((prev) => ({ ...prev, isOpen: false }));
          } catch (error) {
            console.error("削除エラー:", error);
            alert("削除中にエラーが発生しました。");
            setModalState((prev) => ({ ...prev, isOpen: false }));
          }
        },
      });
    };

    // 日付が存在しない場合のフォールバック
    const safeDate = job.printOrderedAt || new Date().toISOString();
    const hasPrintError = isPrintJobError(job.status);

    const printButton = hasPrintError ? (
      <button
        type="button"
        disabled
        className="bg-gray-300 text-gray-500 font-bold px-4 py-2 rounded-md cursor-not-allowed"
        onClick={(e) => e.stopPropagation()}
      >
        印刷不可
      </button>
    ) : (
      <button
        onClick={(e) => {
          e.stopPropagation();
          handlePrint(job);
        }}
        className="bg-white text-primary font-bold px-4 py-2 rounded-md"
      >
        印刷
      </button>
    );

    return (
      <div
        className={`py-4 px-6 cursor-pointer transition-colors duration-200 rounded-md mb-2 flex items-center ${
          hasPrintError ? "min-h-[72px] h-auto" : "h-[72px]"
        } ${
          isSelected
            ? "bg-primary text-white"
            : isCompleted
            ? "hover:bg-gray-300 bg-[#D4D4D4] text-[#0000004D]"
            : "hover:bg-gray-50 bg-white"
        }`}
        onClick={onSelect}
      >
        <div className="flex flex-col w-full gap-2">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center w-2/3">
              <div
                className={`font-bold text-body mr-6 w-1/3 ${
                  isSelected ? "text-white" : ""
                }`}
              >
                {job.targetName}
              </div>
              <div
                className={`text-body w-2/3 ${isSelected ? "text-white" : ""}`}
              >
                {job.description || "タイトルなし"}
              </div>
            </div>
            <div className="flex items-center justify-end w-1/3">
              {!isSelected && !isCompleted && (
                <>
                  <div
                    className={`text-body w-20 text-right mr-6 ${
                      isSelected ? "text-white" : ""
                    }`}
                  >
                    {job.questionCount}問
                  </div>
                  <div
                    className={`text-body w-16 text-right ${
                      isSelected ? "text-white" : ""
                    }`}
                  >
                    {formatDate(safeDate)}
                  </div>
                </>
              )}
            {!isSelected && isCompleted && (
              <>
                <div
                  className={`text-body w-20 text-right mr-6 ${
                    isSelected ? "text-white" : ""
                  }`}
                >
                  {formatDate(safeDate)}
                </div>
                <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              </>
            )}
              {isSelected && !isCompleted && (
                <>
                  <button onClick={handleDelete} className="text-white mr-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 6h18"></path>
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                    </svg>
                  </button>
                  {printButton}
                </>
              )}
              {isSelected && isCompleted && (
                <>
                  <div className="text-white w-20 text-right mr-4">
                    {formatDate(safeDate)}
                  </div>
                  {printButton}
                </>
              )}
            </div>
          </div>
          {hasPrintError && (
            <p
              className={`text-small ${
                isSelected ? "text-white" : "text-red-600"
              }`}
            >
              {PDF_GENERATION_FAILED_MESSAGE}
            </p>
          )}
        </div>
      </div>
    );
  };

  // ジョブリストを表示するコンポーネント
  const JobList = ({ jobs, title }: { jobs: PrintJob[]; title: string }) => {
    const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
    const [isReloading, setIsReloading] = useState(false);

    const handleJobSelect = (jobId: string) => {
      setSelectedJobId(jobId === selectedJobId ? null : jobId);
    };

    const isCompleted = title === "印刷済み";

    const handleReload = async () => {
      setIsReloading(true);
      try {
        if (isCompleted) {
          await refetchPrinted();
        } else {
          await refetch();
        }
      } catch (error) {
        console.error("再読み込みエラー:", error);
      } finally {
        setIsReloading(false);
      }
    };

    const handleClearCompleted = () => {
      setModalState({
        isOpen: true,
        title: "印刷済みジョブのクリア",
        message: "印刷済みのジョブをすべてクリアしますか？",
        onConfirm: async () => {
          try {
            // 印刷済みジョブをクリア
            await clearPrintedJobs.execute();

            // モーダルを閉じる
            setModalState((prev) => ({ ...prev, isOpen: false }));
          } catch (error) {
            alert("印刷済みジョブのクリア中にエラーが発生しました。");
          } finally {
            setModalState((prev) => ({ ...prev, isOpen: false }));
          }
        },
      });
    };

    return (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4 px-2">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            className="text-primary flex items-center"
            onClick={isCompleted ? handleClearCompleted : handleReload}
            disabled={isReloading || clearPrintedJobs.loading}
          >
            {(isReloading || (isCompleted && clearPrintedJobs.loading)) && (
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {isCompleted ? "印刷済みをクリア" : "再読み込み"}
          </button>
        </div>
        {jobs.length === 0 ? (
          <p className="text-gray-500 px-2">ジョブはありません</p>
        ) : (
          <div className="bg-[#e9e9e9] rounded-md p-2">
            {jobs.map((job) => (
              <PrintJobItem
                key={job.id}
                job={job}
                isSelected={selectedJobId === job.id}
                onSelect={() => handleJobSelect(job.id)}
                showPrintButton={true}
                isCompleted={isCompleted}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* ヘッダー */}
      <header className="bg-primary h-[55px] flex items-center justify-between px-6 shadow-md">
        <div className="flex items-center">
          <img
            src="/logo_dailytry.svg"
            alt="ロゴ"
            className="h-8 w-auto mr-3"
          />
          <h2 className="text-heading-2 text-white font-semibold">
            プリントジョブ
          </h2>
        </div>
        <div className="flex items-center">
          <span className="text-white mr-4">
            印刷コード: {authState?.printOffice.printCode || "------"}
          </span>
          <div className="relative" ref={dropdownRef}>
            <button
              className="bg-white text-primary font-bold px-4 py-2 rounded-md flex items-center"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              {authState?.printOffice.name || "教室名"}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ml-2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {showDropdown && (
              <div className="absolute right-0 mt-1 w-[118px] h-[36px] bg-white rounded-[9px] shadow-lg z-10">
                <button
                  className="w-full h-full text-black text-center"
                  onClick={handleLogout}
                >
                  ログアウト
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="flex-grow bg-[#e9e9e9] p-6">
        <div className="max-w-6xl mx-auto">
          {/* 印刷待機中のジョブ */}
          <JobList jobs={waitingJobs} title="印刷待機中" />

          {/* 印刷済みのジョブ */}
          <JobList jobs={completedJobs} title="印刷済み" />
        </div>
      </main>

      {/* 確認モーダル */}
      <ConfirmModal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        onConfirm={modalState.onConfirm}
        onCancel={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default PrintJobsPage;
